import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import { apiClient } from '../lib/axios';

export type LegalDocumentType = 'dpa' | 'security-measures';

const LEGAL_DOCUMENT_FILENAMES: Record<LegalDocumentType, string> = {
  dpa: 'accord-dpa.pdf',
  'security-measures': 'mesures-de-securite.pdf',
};

const triggerBrowserDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const legalDocumentsApi = {
  download: async (type: LegalDocumentType): Promise<void> => {
    const { data } = await apiClient.get<Blob>(`/legal-documents/${type}`, {
      responseType: 'blob',
    });
    triggerBrowserDownload(data, LEGAL_DOCUMENT_FILENAMES[type]);
  },

  downloadVersion: async (fileName: string): Promise<void> => {
    const { data } = await apiClient.get<Blob>(`/legal-documents/file/${fileName}`, {
      responseType: 'blob',
    });
    triggerBrowserDownload(data, fileName);
  },
};

export const useDownloadLegalPdf = () => {
  return useMutation({
    mutationFn: (type: LegalDocumentType) => legalDocumentsApi.download(type),
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useDownloadLegalVersion = () => {
  return useMutation({
    mutationFn: (fileName: string) => legalDocumentsApi.downloadVersion(fileName),
    onError: () => toast.error(i18next.t('common.error')),
  });
};
